import Button from '../../../../components/button'
import Col from 'react-bootstrap/lib/Col'
import Currency from '../../../../components/currency'
import Dropdown from '../../../../components/dropdown'
import Icon, {icons} from '../../../../components/icon'
import Panel from 'react-bootstrap/lib/Panel'
import PropTypes from 'prop-types'
import React, {Component} from 'react'
import Row from 'react-bootstrap/lib/Row'
import SizingChart from './sizingChart'
import {StatusInput} from '../../../../components/formik'
import {colors, media, text} from '../../../../styles'
import {css} from 'glamor'

const style = {
  price: {
    header: css(text.small, {
      color: colors.gray46,
      margin: '2px 0 0'
    }),
    content: css(text.h3, {
      display: 'block',
      margin: '0 0 10px'
    }),
    saleContent: css({
      margin: '0 0 10px'
    }),
    sale: css(text.h3, {
      color: colors.saleRed,
      display: 'block'
    }),
    original: css({
      color: colors.black,
      textDecoration: 'line-through'
    }),
    savings: css({
      color: colors.salemGreen
    })
  },
  sizingChart: {
    body: (fitNote) =>
      css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        ...(!fitNote
          ? media.desktop({
              margin: '30px 0 0'
            })
          : {})
      }),
    icon: css({
      margin: '0 0 0 4px'
    }),
    toggle: css(text.small, {
      textTransform: 'uppercase'
    })
  },
  sizingChartBody: css({
    padding: 0,
    overflowX: 'scroll'
  }),
  sizingChartPanel: css({
    border: 'none',
    boxShadow: 'none',
    fontSize: '12px',
    lineHeight: '21px',
    margin: 0
  }),
  wrapper: css({
    ...media.xs({
      margin: '0 0 30px'
    }),
    ...media.sm({
      margin: '15px 60px 30px'
    }),
    ...media.desktop({
      margin: '10px 0 20px'
    })
  })
}

const PriceSection = ({price, salePrice, quantity}) => (
  <div className="price">
    <h5 {...style.price.header}>
      <strong>
        Price:
        {quantity > 1 && (
          <span>
            {' '}
            (<Currency amount={salePrice || price} /> ea.)
          </span>
        )}
      </strong>
    </h5>
    {salePrice ? (
      <div {...style.price.saleContent}>
        <span {...style.price.sale}>
          <Currency amount={salePrice * quantity} />
        </span>
        <span {...style.price.original}>
          <Currency amount={price * quantity} />
        </span>{' '}
        <span {...style.price.savings}>
          You Save: <Currency amount={(price - salePrice) * quantity} />
        </span>
      </div>
    ) : (
      <span className="priceContent" {...style.price.content}>
        <Currency amount={price * quantity} />
      </span>
    )}
  </div>
)

PriceSection.propTypes = {
  price: PropTypes.number.isRequired,
  salePrice: PropTypes.number,
  quantity: PropTypes.number.isRequired
}

class SizeChartSection extends Component {
  constructor(props) {
    super(props)

    this.state = {
      shouldShowSizingChart: false
    }
  }

  toggleSizingChart = () => {
    this.setState((state) => ({
      shouldShowSizingChart: !state.shouldShowSizingChart
    }))
  }

  render() {
    const {fitNote, sizeChart} = this.props
    const {shouldShowSizingChart} = this.state

    return (
      <>
        <Col xs={12} md={fitNote ? 12 : 4}>
          <div {...style.sizingChart.body(!!fitNote)}>
            {fitNote ? <div className="text-danger">FIT: {fitNote}</div> : null}
            <div className="text-right">
              <Button
                kind="link"
                onClick={this.toggleSizingChart}
                style={style.sizingChart.toggle}
              >
                <strong>
                  {shouldShowSizingChart ? 'Hide' : 'View'} size chart
                </strong>
                <Icon
                  icon={
                    shouldShowSizingChart ? icons.chevronUp : icons.chevronDown
                  }
                  {...style.sizingChart.icon}
                />
              </Button>
            </div>
          </div>
        </Col>
        <Col xs={12}>
          <Panel
            expanded={shouldShowSizingChart}
            onToggle={() => null}
            {...style.sizingChartPanel}
          >
            <Panel.Body collapsible {...style.sizingChartBody}>
              <SizingChart sizeChart={sizeChart} />
            </Panel.Body>
          </Panel>
        </Col>
      </>
    )
  }
}

SizeChartSection.propTypes = {
  fitNote: PropTypes.string,
  sizeChart: PropTypes.array.isRequired
}

class SizeDropdownSection extends Component {
  static SizeDropdownRenderer({Name, Surcharge, Available}) {
    return {
      disabled: !Available,
      value: Name,
      label: Available
        ? Surcharge
          ? `${Name} - $${Surcharge.toFixed(2)}`
          : Name
        : `${Name} - Out of Stock`
    }
  }

  onChange = (event) => {
    event.target.value = event.target.value.split('-')[0].trim()

    return this.props.onChange(event)
  }

  render() {
    const {errors, options, touched, values} = this.props

    return (
      <StatusInput
        inputComponent={Dropdown}
        errors={errors}
        id="size"
        label="Size"
        name="Size"
        onChange={this.onChange}
        options={options.map((option) =>
          SizeDropdownSection.SizeDropdownRenderer(option)
        )}
        size="block"
        touched={touched}
        values={values}
      />
    )
  }
}

SizeDropdownSection.propTypes = {
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      Name: PropTypes.string.isRequired,
      Surcharge: PropTypes.number.isRequired,
      Available: PropTypes.bool.isRequired
    })
  ).isRequired,
  touched: PropTypes.object.isRequired,
  values: PropTypes.object,
  licensingMarkup: PropTypes.number
}

const PurchaseForm = ({
  cta,
  errors,
  fitNote,
  handleChange,
  handleSubmit,
  setFieldValue,
  price,
  salePrice,
  sizeChart,
  sizes,
  isSubmitting,
  touched,
  values,
  licensingMarkup,
  AdminOrderChange
}) => {
  return (
    <div {...style.wrapper}>
      <form className="purchaseForm" onSubmit={handleSubmit}>
        <Row>
          <Col xsHidden smHidden md={8} lg={8}>
            <PriceSection
              price={price}
              salePrice={salePrice}
              quantity={values.Quantity}
            />
          </Col>
          {sizeChart.length ? (
            <SizeChartSection fitNote={fitNote} sizeChart={sizeChart} />
          ) : null}
          <Col xs={12}>
            <div className="size">
              <SizeDropdownSection
                errors={errors}
                onChange={handleChange}
                options={sizes}
                touched={touched}
                values={values}
                licensingMarkup={licensingMarkup}
              />
            </div>
          </Col>
          <Col xs={8} sm={8} mdHidden lgHidden>
            <PriceSection
              price={price}
              salePrice={salePrice}
              quantity={values.Quantity}
            />
          </Col>
          <Col xs={4} sm={4} md={4} lg={4}>
            <div className="quantity">
              <StatusInput
                inputComponent={Dropdown}
                errors={errors}
                id="quantity"
                label="Quantity"
                name="Quantity"
                onChange={({target}) =>
                  setFieldValue(target.name, parseInt(target.value, 10))
                }
                options={[1, 2, 3, 4, 5, 6]}
                size="block"
                touched={touched}
                values={values}
              />
            </div>
          </Col>
          <Col xs={12} sm={12} md={8} lg={8}>
            <Button
              block
              disabled={isSubmitting}
              size="large"
              type="submit"
              kind={!AdminOrderChange ? 'cta' : 'primary'}
            >
              {!AdminOrderChange ? cta : 'Add To Order'}
            </Button>
          </Col>
        </Row>
      </form>
    </div>
  )
}

PurchaseForm.propTypes = {
  cta: PropTypes.string.isRequired,
  errors: PropTypes.shape({
    Quantity: PropTypes.string,
    Size: PropTypes.string
  }),
  fitNote: PropTypes.string,
  handleChange: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  price: PropTypes.number.isRequired,
  salePrice: PropTypes.number,
  licensingMarkup: PropTypes.number,
  setFieldValue: PropTypes.func,
  sizeChart: PropTypes.array.isRequired,
  sizes: PropTypes.array.isRequired,
  status: PropTypes.shape({
    success: PropTypes.bool
  }),
  touched: PropTypes.shape({
    Quantity: PropTypes.bool,
    Size: PropTypes.bool
  }),
  values: PropTypes.shape({
    Quantity: PropTypes.number,
    Size: PropTypes.string
  })
}

export default PurchaseForm
